<template>
  <div ref="header" class="main-header">
    <div ref="logo-box" class="logo-wrap">
      <router-link :to="{name: 'home'}" class="nav-item logo-link">
        <a-b-a-logo ref="logo" class="main block" />
      </router-link>
      <air-salon-banner />
      <div class="separator"></div>
      <bookstop-banner />
      <div class="separator"></div>
      <uq-bar-banner />
    </div>
    <nav>
      <router-link
        v-if="aboutPageRouterTo"
        :to="aboutPageRouterTo"
        class="nav-item secondary-item flex justify-center">
        About
      </router-link>
      <template v-if="showPostFilters">
      </template>
      <user-menu class="user-menu" />
    </nav>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import UserMenu from './UserMenu'
import ABALogo from './ABALogo'
import AirSalonBanner from './AirSalonBanner.vue'
import BookstopBanner from './BookstopBanner.vue'
import UqBarBanner from './UqBarBanner.vue'

export default {
  name: 'MainHeader',
  components: { ABALogo, UserMenu, AirSalonBanner, BookstopBanner, UqBarBanner },
  data: () => ({
    logoIsSmall: false
  }),

  computed: {
    ...mapState(['user', 'showDraftsInGrid', 'menu']),
    ...mapGetters(['pageRouteByTitle']),
    adminOrEditor () {
      return !!this.user && (this.user.role === 'admin' || this.user.role === 'editor')
    },
    admin () {
      return this.user && this.user.role === 'admin'
    },
    showPostFilters () {
      return !!this.user
    },
    showDraftsMessage () {
      return this.showDraftsInGrid ? 'Hide Drafts' : `Show ${!this.adminOrEditor ? 'Mine ' : ''}Drafts`
    },

    aboutPageRouterTo () {
      return this.pageRouteByTitle('about')
    }
  },

  mounted () {
    window.addEventListener('scroll', this.setLogoSize)
    window.addEventListener('resize', this.setLogoSize)
    // Set mobile banner styles immediately to prevent flash
    if (window.innerWidth <= 480 && this.$refs['logo-box']) {
      const logoBox = this.$refs['logo-box']
      // Disable transition on parent
      logoBox.style.setProperty('transition', 'background 0.2s', 'important')
      // Set banner styles with !important
      const banners = logoBox.querySelectorAll('.air-salon-banner, .bookstop-banner, .uq-bar-banner')
      const separators = logoBox.querySelectorAll('.separator')
      banners.forEach(banner => {
        banner.style.setProperty('height', '60px', 'important')
        banner.style.setProperty('min-height', '60px', 'important')
        banner.style.setProperty('max-height', '60px', 'important')
        banner.style.setProperty('transition', 'none', 'important')
        banner.style.setProperty('padding-left', '0.25rem', 'important')
        banner.style.setProperty('padding-right', '0.25rem', 'important')
      })
      separators.forEach(separator => {
        separator.style.setProperty('height', '60px', 'important')
        separator.style.setProperty('margin', '0 0.25rem', 'important')
      })
      // Also set on child logo elements
      const logoElements = logoBox.querySelectorAll('.airsalon-logo, .bookstop-logo, .uqbar-logo')
      logoElements.forEach(el => {
        el.style.setProperty('height', '60px', 'important')
        el.style.setProperty('min-height', '60px', 'important')
        el.style.setProperty('max-height', '60px', 'important')
        el.style.setProperty('padding', '0.25rem', 'important')
        el.style.setProperty('transition', 'none', 'important')
      })
    }
  },

  methods: {
    ...mapActions(['logOut', 'toggleDraftsInGrid']),
    setLogoSize () {
      if (!this.$refs['logo-box']) return
      // Don't apply scaling on mobile - banners should stay fixed
      if (window.innerWidth <= 480) return
      const style = getComputedStyle(document.documentElement)
      const mainH = style.getPropertyValue('--logo-main-height')
      const rem = 16 // px
      const mainHpx = parseFloat(mainH) * rem
      if (window.scrollY > mainHpx / 4) {
        if (!this.logoIsSmall) {
          this.$refs['logo-box'].classList.add('small')
          this.logoIsSmall = true
        }
      } else {
        if (this.logoIsSmall) {
          this.$refs['logo-box'].classList.remove('small')
          this.logoIsSmall = false
        }
      }
      if (this.logoIsSmall) return
      requestAnimationFrame(() => {
        if (!this.$refs['logo-box'] || !this.$refs.logo) return
        const s = window.scrollY
        const curScale = style.getPropertyValue('--logo-current-scale')
        const startH = parseFloat(mainH) * rem
        const endH = 3 * rem
        const t = s / (startH - endH)
        let scale = 1
        if (t < 1) {
          const curH = startH * (1 - t) + endH * t
          scale = curH / startH
        } else {
          scale = endH / startH
        }
        if (curScale !== `${scale}`) {
          document.documentElement.style.setProperty('--logo-current-scale', `${scale}`)
        }
      })
    }
  }
}
</script>

<style lang='scss'>
  @import "../../styles/vars";
  @import "../../styles/mixins";
  #app {
    .main-header{
      display: flex;
      align-items: flex-end;
      padding: 0;
      z-index: $z-index-header;
      pointer-events: none;
      min-width: 0;
      flex-shrink: 0;
      width: 100%;
      max-width: 100%;
      overflow: visible;
      box-sizing: border-box;

      @include wider-then-phone {
        width: 100%;
        max-width: 100%;
        overflow: visible;
      }

      .logo-wrap {
        align-self: stretch;
        display: flex;
        align-items: center;
        height: calc(var(--logo-main-height) * var(--logo-current-scale));
        pointer-events: all;
        transition: background 0.2s, height 0.2s;
        flex-shrink: 1;
        flex-grow: 0;
        min-width: 0;
        overflow: hidden;

        @include wider-then-phone {
          max-width: calc(100% - 200px);
        }

        @media (max-width: $max-width-phone) {
          max-width: calc(100% - 120px);
        }

        @media (max-width: 30rem) {
          transition: background 0.2s !important;
          height: 60px !important;

          .air-salon-banner,
          .bookstop-banner,
          .uq-bar-banner {
            height: 60px !important;
            min-height: 60px !important;
            max-height: 60px !important;
            transition: none !important;
          }
        }
        a {
          text-decoration: none;
        }
        .air-salon-banner {
          height: 60px;
          min-height: 60px;
          max-height: 60px;
          transition: none;
          padding-left: 0.25rem;
          padding-right: 0.25rem;
          border-right: 1px solid $color-aba-blue;

          @include wider-then-phone {
            height: calc(var(--logo-main-height) * var(--logo-current-scale) * 0.55);
            min-height: auto;
            max-height: none;
            padding-left: calc(0.5rem * var(--logo-current-scale));
            padding-right: 0;
            transition: height 0.2s, width 0.2s;
          }
        }
        .bookstop-banner {
          height: 60px;
          min-height: 60px;
          max-height: 60px;
          transition: none;
          padding-left: 0.25rem;
          padding-right: 0.25rem;

          @include wider-then-phone {
          height: calc(var(--logo-main-height) * var(--logo-current-scale) * 0.55);
            min-height: auto;
            max-height: none;
          padding-left: calc(0.5rem * var(--logo-current-scale));
            padding-right: 0;
          transition: height 0.2s, width 0.2s;
          }
        }
        .uq-bar-banner {
          height: 60px;
          min-height: 60px;
          max-height: 60px;
          transition: none;
          padding-left: 0.25rem;
          padding-right: 0.25rem;

          @include wider-then-phone {
          height: calc(var(--logo-main-height) * var(--logo-current-scale) * 0.55);
            min-height: auto;
            max-height: none;
          padding-left: calc(0.5rem * var(--logo-current-scale));
            padding-right: 0;
          transition: height 0.2s, width 0.2s;
          }
        }
        .separator {
          width: 1px;
          height: 60px;
          background-color: $color-aba-blue;
          margin: 0 0.25rem;

          @include wider-then-phone {
            height: calc(var(--logo-main-height) * var(--logo-current-scale) * 0.55);
          margin: 0 0.5rem;
          }
        }
        .logo-link {
          display: flex;
          height: calc(var(--logo-main-height) * var(--logo-current-scale) * 0.75);
          justify-content: center;
          background: radial-gradient(closest-side, #4b4b4b, $color-bg-semitransparent);
          border: solid $color-aba-blue;
          border-width: 0 1px 0 0;
          padding: 0 0.5rem;
          color: $color-aba-blue;

          transition: background 0.2s, height 0.2s;

          &:hover {
            background: radial-gradient(closest-side, #b9b9b9, $color-bg-semitransparent);
          }

          & > span {
            font-size: calc(var(--logo-main-height) * var(--logo-current-scale) * 0.75 * 0.95);
            height: calc(var(--logo-main-height) * var(--logo-current-scale) * 0.75);
            width: calc(var(--logo-main-height) * var(--logo-current-scale) * 0.75 * #{$logo-ratio});
            transition: font-size 0.2s, height 0.2s, width 0.2s;
          }
        }
        &.small {
          height: $base-size;
          .logo-link {
            height: calc(#{$base-size} * 0.75);
            background: radial-gradient(closest-side, #fff, $color-bg-semitransparent);
            & > span {
              font-size: 2rem;
              height: 100%;
              width: calc(#{$base-size} * 0.75 * #{$logo-ratio});
              transition: font-size 0.2s, height 0.2s, width 0.2s;
            }
          }
        }
      }
      nav {
        display: -moz-box !important; /* Firefox */
        display: -ms-flexbox !important; /* IE */
        display: -webkit-flex !important; /* Safari */
        display: flex !important;
        align-items: center;
        margin-left: auto;
        flex-shrink: 0;
        flex-grow: 0;
        min-width: 100px;
        max-width: none;
        pointer-events: all;
        position: relative;
        z-index: $z-index-header + 1;
        visibility: visible !important;
        opacity: 1 !important;
        overflow: visible;

        @include wider-then-phone {
          display: -moz-box !important; /* Firefox */
          display: -ms-flexbox !important; /* IE */
          display: -webkit-flex !important; /* Safari */
          display: flex !important;
          visibility: visible !important;
          opacity: 1 !important;
          min-width: 150px;
          flex-shrink: 0;
        }

        a {
          &.nav-item {
            display: flex;
            align-items: center;
            height: $base-size * 0.75;
            background: $color-bg-semitransparent;
            padding: $base-padding /2 $base-padding;
          }
        }
        .nav-item.secondary-item {
          display: flex;
          @include wider-then-phone {
            display: none;
          }
        }
      }

      .login {
        flex-shrink: 0;
        margin-left: auto;
        pointer-events: all;
      }

    }

    .user-actions {
      padding: $base-padding / 2;
    }
  }
</style>
